import { onBeforeMount, onBeforeUnmount } from 'vue'
import navigationMonitor from '../services/NavigationMonitorService'

/**
 * Bridges the browser Back/Forward buttons to the app's virtual router (goBack/goForward)
 * using the legacy three-entry history sentinel trick: keep [back, current, forward] history
 * entries and sit on `current`; a popstate onto `back`/`forward` dispatches a `previous`/`next`
 * DOM event and snaps back to `current`.
 *
 * @param {import('vue').Ref<{ goBack: Function, goForward: Function }>} customNavigation
 */
export function useBrowserHistoryBridge(customNavigation) {
  function handleHistoryPopstate(event) {
    const state = event.state
    if (state === 'current') return

    if (state === 'back' || state === 'forward') {
      // Marks the browser-driven origin so the navigation monitor can tell a
      // user Back/Forward apart from programmatic navigation that follows it.
      navigationMonitor.markPopstate(state === 'forward' ? 'forward' : 'back')
      const navEvent = document.createEvent('Event')
      navEvent.initEvent(state === 'forward' ? 'next' : 'previous', true, true)
      window.dispatchEvent(navEvent)
      if (state === 'forward') {
        history.go(-1)
      } else {
        history.go(1)
      }
      return
    }

    // Unknown state: the sentinel was corrupted (e.g. a replaceState({}) wiped the
    // 'current' label) or the user backed past the sentinel. Dispatching for unknown
    // states is what caused the infinite Back↔Forward loop: landing on a corrupted
    // 'current' dispatched previous + go(1) onto 'forward', which dispatched next +
    // go(-1) back onto the corrupted entry, forever. Re-anchor here instead — for the
    // corrupted-'current' case this restores the sentinel exactly; for the
    // backed-past case it stops the cascade at worst one entry off, never a loop.
    history.replaceState('current', null)
    navigationMonitor.reportAnomaly({
      type: 'history-sentinel-corrupted',
      priority: 'medium',
      fingerprintKey: 'sentinel',
      message: 'History sentinel hit an unknown popstate state — re-anchored to current',
      details: { unknownState: typeof state === 'string' ? state : typeof state }
    })
  }

  const handleVirtualForward = () => customNavigation.value.goForward()
  const handleVirtualBack = () => customNavigation.value.goBack()

  onBeforeMount(() => {
    if (window.history && history.pushState) {
      history.pushState('back', null)
      history.pushState('current', null)
      history.pushState('forward', null)
      history.go(-1)
      window.onpopstate = handleHistoryPopstate
    }
    window.addEventListener('next', handleVirtualForward, false)
    window.addEventListener('previous', handleVirtualBack, false)
  })

  // Symmetric teardown: without this the 'next'/'previous' listeners and window.onpopstate
  // leak on every mount/unmount cycle (logout -> login is SPA, not a reload), so a single
  // browser Back would fire goBack() once per leaked listener — the multi-step Back/Forward
  // loop the client reported.
  onBeforeUnmount(() => {
    window.removeEventListener('next', handleVirtualForward, false)
    window.removeEventListener('previous', handleVirtualBack, false)
    if (window.onpopstate === handleHistoryPopstate) {
      window.onpopstate = null
    }
  })
}
